import { type Currency, GetCurrencyByString, getCurrencySymbol } from '@lib/services'
import { MytennightsTestId } from '@lib/testing'
import { type Cause, isMultiCharity, MTDtracking, QurbaniCategory, useAuthWithSignup, useCheckoutPlan } from '@shamaazi/mytennights'
import { type FC, useRef } from 'react'
import CurrencyInput from 'react-currency-input-field'

export const CauseSplitInput: FC<{ deletedAmountError: string, minimumAmountError: string, setDeletedAmountError: (error: string) => void, setMinimumAmountError: (error: string) => void }> = ({ deletedAmountError, minimumAmountError, setDeletedAmountError, setMinimumAmountError }) => {
  const { plan, setPlan } = useCheckoutPlan()
  const { user } = useAuthWithSignup()
  const setCauseValue = (causeID: string, value: number): void => {
    // Set error if all other causes have amount 0 and the current cause is being set to 0
    const { [causeID]: currentCause, ...restCauses } = plan.causes
    if (Object.values(restCauses).every(causeAmount => causeAmount === 0) && value === 0) {
      setDeletedAmountError('Please enter your donation amount to proceed.')
    } else {
      setMinimumAmountError('')
      setDeletedAmountError('')
    }

    const newCauses = { ...plan.causes, [causeID]: value }
    setPlan({ ...plan, causes: newCauses })
  }

  const causes = plan.selectedCauses.length > 0
    ? plan.selectedCauses
    : plan.charity.causes.filter(c => c.category !== QurbaniCategory && (!isMultiCharity(plan.charity.charity_id) || ['Where need is the Greatest', 'Zakat', 'Sadaqah'].includes(c.title)))

  return <>
        <div className="flex flex-col gap-y-2">
            {causes.map((cause) => {
              const value = plan.causes[cause.cause_id] ?? 0
              return <CauseInput
              key={cause.cause_id}
              cause={cause}
              currency={plan.charity.currency}
              value={value}
              onChange={(value) => {
                setCauseValue(cause.cause_id, value)
              }}
              trackEvent={() => {
                MTDtracking.addCause(user?.email, cause.title, value, plan)
              }}
              />
            })}
        </div>
        {deletedAmountError && <p className="text-mtn-red-100 text-xs font-normal mt-2">{deletedAmountError}</p>}
        {!deletedAmountError && minimumAmountError && <p className="text-mtn-red-100 text-xs font-normal mt-2">{minimumAmountError}</p>}
    </>
}

interface CauseInputProps {
  cause: Cause
  value: number
  currency: Currency
  onChange: (v: number) => void
  trackEvent: () => void
}

const CauseInput: FC<CauseInputProps> = ({ cause, value, onChange, currency, trackEvent }) => {
  const input = useRef<HTMLInputElement>(null)

  const updateAmount = (value: string): void => {
    const n = Number.parseFloat(value)
    const v = (!Number.isNaN(n) && n > 0 && Math.round(n * 100)) || 0
    if (v < 0 || v > 99999999) {
      return
    }
    onChange(v)
  }

  return <div
    data-test-id={MytennightsTestId.cause_ + cause.title.toLowerCase()}
    className='font-medium col-span-1 flex flex-row justify-between items-center'>

    {/* Cause Title */}
    <div className='text-mtd-label text-mtd-secondary-4 font-semibold'>{cause.title}</div>

    {/* Donation amount input */}
    <div className={`flex flex-row rounded-lg h-12
          cursor-pointer
          p-4 pr-3 items-center bg-white bg-opacity-60 border border-mtd-indigo-3`}>
      <span className="whitespace-nowrap text-mtd-label text-mtd-gray-6 font-medium mr-2">{getCurrencySymbol(GetCurrencyByString(currency))}</span>
      <CurrencyInput
        data-test-id={MytennightsTestId.causeInput_ + cause.title.toLowerCase()}
        defaultValue={value > 0 ? value / 100 : ''}
        id="causeAmount"
        className={`${value === 0 ? 'text-mtd-gray-5' : 'text-mtd-secondary-5'} w-mtd-cause-input-73 font-semibold text-left bg-transparent focus:outline-none`}
        placeholder="0"
        step={0.01}
        onValueChange={(value) => updateAmount(value ?? '')}
        ref={input}
        onBlur={trackEvent}
      />
    </div>
  </div>
}
